import { filter_data, get_overall_average } from "reports/Tag/calculations";
import { calculate_engagement_by_anchor } from "./caluclate_engagement";

// Utility function to check if a value is a valid number (not 'N/A' or other non-numeric values)
// This function filters out various forms of 'N/A' values that might appear in survey responses
// and ensures only valid numeric responses are used in calculations
function isValidNumericResponse(value) {
    return value !== null && 
           value !== undefined && 
           value !== 'N/A' && 
           value !== 'n/a' && 
           value !== 'NA' && 
           value !== 'na' && 
           typeof value === 'number' && 
           !isNaN(value);
}

// Utility function to parse JSON responses from GPT
function parseGPTJsonResponse(gptResponse) {
    try {
        // Remove markdown code block delimiters if they exist
        let cleanedResponse = gptResponse.trim();
        
        // Remove ```json and ``` markers
        if (cleanedResponse.startsWith('```json')) {
            cleanedResponse = cleanedResponse.replace(/^```json\s*/i, '');
        }
        if (cleanedResponse.startsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/^```\s*/, '');
        }
        if (cleanedResponse.endsWith('```')) {
            cleanedResponse = cleanedResponse.replace(/\s*```$/, '');
        }
        
        // Parse the JSON
        const parsedJson = JSON.parse(cleanedResponse);
        return parsedJson;
    } catch (error) {
        console.error("Error parsing GPT JSON response:", error);
        console.error("Original response:", gptResponse);
        
        // Return a fallback structure if parsing fails
        return {
            overview: "Error parsing response",
            quotes: [],
            insights: "Unable to parse insights from response",
            conclusion: "Response parsing failed"
        };
    }
}

// LLM API function - supports multiple models including OpenAI and Grok
async function callLLM(userPrompt, onTokenUpdate = null, model = "gpt-4.1") {
  const scrambled = ["sk", "-", "svcacct","-", "i4THn1qxJ18sYOQJhRJLRH2wiTTxp1kx2_nQwzyeJtY4cyRpF16A3iv-6u7I3kDXqPNjVNpsRhT3BlbkFJmGpLTOFc-llkrsv0AutTgfauJQ5l0AcHyMNlZBPffbddq29MDb5n9vduzlaTrl3AZ4AcYl_usA"].join("");

  const code_to_use = scrambled;
    try {
        let apiUrl, headers, requestBody;
        
        // Configure API based on selected model
        if (model === "grok-4-latest") {
            // Grok API configuration
            apiUrl = "https://api.x.ai/v1/chat/completions";
            headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer xai-LaVvLzCLdUTPt2ns9qNaPMz0BWJK8mwoHGlQlvZvUpeFbgZKyppsISdSnMIJbYDCKxdIAUeIT27SKlt9`,
            };
            requestBody = {
                model: "grok-4-latest",
                messages: [
                    { role: "user", content: userPrompt },
                ],
                stream: false,
                temperature: 0.7
            };
        } else {
            // OpenAI API configuration (default)
            apiUrl = "https://api.openai.com/v1/chat/completions";
            headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${code_to_use}`,
            };
            requestBody = {
                model: model,
                seed: 1,
                messages: [
                    { role: "user", content: userPrompt },
                ],
                stream: false,
            };
            {
                const mentionsJson = /json/i.test(userPrompt);
                const mentionsJsonArray = /json\s*array/i.test(userPrompt);
                if (mentionsJson && !mentionsJsonArray) {
                    requestBody.response_format = { type: "json_object" };
                }
            }
        }

        const response = await fetch(apiUrl, {
            method: "POST",
            headers: headers,
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            throw new Error(`LLM API responded with status ${response.status}`);
        }

        const data = await response.json();
        const assistantMessage = data.choices?.[0]?.message?.content || "";
        const usage = data.usage || {};
        
        const tokenInfo = {
            prompt_tokens: usage.prompt_tokens || 0,
            completion_tokens: usage.completion_tokens || 0,
            total_tokens: usage.total_tokens || 0
        };

        // Call the token update callback if provided
        if (onTokenUpdate) {
            onTokenUpdate(tokenInfo);
        }

        return {
            content: assistantMessage,
            tokens: tokenInfo
        };
    } catch (error) {
        console.error("Error calling LLM:", error);
        throw error;
    }
}

export async function calculateSummary(raw_data, core_data, debriefSchedules, anchorParentID, onTokenUpdate = null, selectedModel = "gpt-4.1", selectedAnchorIDs = null, combineAnchors = false) {
    const questions = core_data.questions;
    const outcomeQuestions = core_data.outcomeQuestions;
    const standards = core_data.standards;

    // Initialize token tracking
    let totalTokenUsage = {
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0
    };

    const updateTokens = (newTokens) => {
        totalTokenUsage.prompt_tokens += newTokens.prompt_tokens;
        totalTokenUsage.completion_tokens += newTokens.completion_tokens;
        totalTokenUsage.total_tokens += newTokens.total_tokens;
        totalTokenUsage.call_count += 1;
        
        // Notify the UI of token updates
        if (onTokenUpdate) {
            onTokenUpdate(totalTokenUsage);
        }
    };

    // Find the selected anchor parent by ID
    const selectedAnchorParent = core_data.categories[0].categories.find(cat => cat.id === anchorParentID);
    
    if (!selectedAnchorParent) {
        throw new Error(`Anchor parent with ID ${anchorParentID} not found`);
    }

    // Calculate overall scores across all data
    const overallLatestScore = get_overall_average(raw_data[0].responses);
    const overallLastScore = get_overall_average(raw_data[0].last);
    const overallChange = overallLatestScore - overallLastScore;

    // Calculate overall factor scores across all data (not filtered by anchor)
    const overallLatestFactorScores = calculateAverageScoresPerFactor(raw_data[0].responses);
    const overallLastFactorScores = calculateAverageScoresPerFactor(raw_data[0].last);
    
    // Get formatted overall factor data with changes
    const overallFactorData = getChangeData(overallLatestFactorScores, overallLastFactorScores, questions);

    // NOTE: Anchor participation rate calculation is temporarily disabled because
    // the employee categories object is now paginated and no longer returns
    // accurate totals for this computation. Once participation logic is updated
    // to handle pagination correctly, restore this call and related usage.
    //
    // const participationRate = calculate_engagement_by_anchor(
    //     debriefSchedules.culture_audit_reports.response,
    //     anchorParentID,
    //     core_data.categories,
    //     core_data.orgCategories
    // );
    const participationRate = [];


    // Collect individual reports for each anchor child
    const anchorReports = [];

    // Determine which anchors to process (all by default, or filtered by selection)
    const optionsToProcess = Array.isArray(selectedAnchorIDs) && selectedAnchorIDs.length > 0
        ? selectedAnchorParent.options.filter(opt => selectedAnchorIDs.includes(opt.id))
        : selectedAnchorParent.options;
    if (combineAnchors && optionsToProcess.length > 1) {
        const filterList = optionsToProcess.map(opt => ({ id: opt.id }));
        const filteredData = filter_data(raw_data[0].responses, filterList);
        const lastFilteredData = filter_data(raw_data[0].last, filterList);

        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        const latestOutcomeScores = calculateAverageScoresPerOutcome(filteredData, outcomeQuestions);
        const lastOutcomeScores = calculateAverageScoresPerOutcome(lastFilteredData, outcomeQuestions);
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);
        const totalFeedbackCount = filteredData.length;
        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData);

        // Treat combined anchors as a single anchor and generate same analysis as per-anchor
        const anchorName = (core_data && (core_data.orgName || core_data.organization?.name || core_data.org?.name || core_data.name)) || "Organization";
        // Aggregate participation across selected anchors to mirror per-anchor schema
        // NOTE: Combined anchor participation is also disabled while anchor
        // participation calculations are turned off.
        // const combinedParticipation = (() => {
        //     const selected = participationRate.filter(p => optionsToProcess.some(o => o.id === p.anchor_id));
        //     const completed = selected.reduce((acc, p) => acc + (p.completed || 0), 0);
        //     const total = selected.reduce((acc, p) => acc + (p.total || 0), 0);
        //     return { anchor_id: "combined", name: anchorName, completed, total, date: selected?.[0]?.date };
        // })();
        const combinedParticipation = null;

        // Identify lowest and top factors
        const factorsByScore = [...changeDataWithFeedback].sort((a, b) => {
            const scoreA = typeof a.latest_score === 'number' ? a.latest_score : 0;
            const scoreB = typeof b.latest_score === 'number' ? b.latest_score : 0;
            return scoreA - scoreB; // Ascending (lowest first)
        });
        const lowest3Factors = factorsByScore.filter(f => typeof f.latest_score === 'number').slice(0, 3);
        const factorsByChange = [...changeDataWithFeedback]
            .filter(f => typeof f.latest_score === 'number' && typeof f.last_score === 'number')
            .sort((a, b) => Math.abs(b.score_change) - Math.abs(a.score_change));
        const hasSignificantChanges = factorsByChange.some(f => Math.abs(f.score_change) >= 0.5);
        const top3FactorsForAnalysis = hasSignificantChanges
            ? factorsByChange.slice(0, 3)
            : [...changeDataWithFeedback]
                .filter(f => typeof f.latest_score === 'number')
                .sort((a, b) => b.latest_score - a.latest_score)
                .slice(0, 3);

        // Detailed LLM analysis for lowest 3
        const detailedFactorAnalysis = [];
        for (const factor of lowest3Factors) {
            const factorAnalysisPrompt = `
            Please analyze the following feedback data for this underperforming culture factor:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            
            **Latest Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Please provide a JSON response with the following structure:
            {
                "what_this_means_for_you": ["Point 1 - one sentence", "Point 2 - one sentence", "Point 3 - one sentence"],
                "day_to_day_impacts": ["Impact 1 - one sentence", "Impact 2 - one sentence", "Impact 3 - one sentence"],
                "impactful_quotes": ["Quote 1", "Quote 2", "Quote 3", "Quote 4", "Quote 5", "Quote 6"]
            }
            
            **Guidelines:**
            - "what_this_means_for_you": Provide exactly 3 points, each as one clear sentence explaining what this low score specifically means in the simplest terms
            - "day_to_day_impacts": Provide exactly 3 points, each as one sentence describing concrete daily effects employees experience due to this factor's low performance
            - "impactful_quotes": Extract up to 6 of the most impactful, meaningful quotes directly from the feedback provided. For English quotes, use the exact wording. For quotes in any other language, you MUST translate the entire quote text to English first, then add "(translated)" at the end. The quote text itself must be in English. If fewer than 6 impactful quotes exist, provide what's available.
            - Be specific and actionable
            - Focus on practical implications
            - Use clear, jargon-free language
            - Base insights on the actual feedback provided
            `;
            const gptResult = await callLLM(factorAnalysisPrompt, updateTokens, selectedModel);
            const parsedAnalysis = parseGPTJsonResponse(gptResult.content);

            // Generate key takeaways
            const factorTakeawaysPrompt = `
            Based on the following data for this specific culture factor, provide 4 key takeaways:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide 4 key takeaways specifically for this factor. Each takeaway should be:
            - A single, clear sentence
            - Specific to this factor's performance and feedback
            - Summarative of the key themes in the feedback
            - Based on the data and feedback provided
            
            Return only a JSON array of strings:
            ["takeaway 1", "takeaway 2", "takeaway 3", "takeaway 4"]
            `;
            const factorTakeawaysResult = await callLLM(factorTakeawaysPrompt, updateTokens, selectedModel);
            const factorTakeaways = parseGPTJsonResponse(factorTakeawaysResult.content);

            // Generate strategic focus
            const factorStrategicFocusPrompt = `
            Based on the following data for this specific culture factor, provide 3 strategic considerations for leadership:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            
            **Factor Challenges from Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide exactly 3 strategic considerations that represent the most important things for leaders to strategize based on all the feedback. Each should be:
            - A concise final thought about what leaders should strategize/consider
            - Based directly on the feedback patterns and themes
            - Actionable strategic guidance (not just tasks)
            - Focused on leadership decision-making and priorities
            
            Return only a JSON array of 3 strings:
            ["strategic consideration 1", "strategic consideration 2", "strategic consideration 3"]
            `;
            const factorStrategicFocusResult = await callLLM(factorStrategicFocusPrompt, updateTokens, selectedModel);
            const factorStrategicFocus = parseGPTJsonResponse(factorStrategicFocusResult.content);

            // Generate one-sentence summary
            const factorSummaryPrompt = `
            Based on the following data for this culture factor, provide a single sentence summary that captures the most important insight:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide one clear, concise sentence that summarizes the most critical insight about this factor's performance. The sentence should:
            - Capture the essence of what's happening with this factor
            - Don't reference the score
            - The simplest sentence that best describes the theme of the feedback for this factor
            - Be easily understood by leadership
            - A Single sentence that isn't too long
            
            Return only the single sentence summary, no additional formatting or explanation.
            `;
            const factorSummaryResult = await callLLM(factorSummaryPrompt, updateTokens, selectedModel);

            // Generate factor definition to match per-anchor behavior
            const factorDefinitionPrompt = `
            Based on the following culture factor and organizational feedback, provide a clear definition of what this factor means for organizations:
            
            **Factor:** ${factor.factor_name}
            
            Create a definition that explains:
            - What this factor represents in the workplace
            
            The definition should be:
            - 1 sentence long
            - Written in clear, professional language
            - Applicable to organizational leaders and HR professionals
            
            Return only the definition, no additional formatting or explanation.
            `;
            const factorDefinitionResult = await callLLM(factorDefinitionPrompt, updateTokens, selectedModel);

            detailedFactorAnalysis.push({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: typeof factor.score_change==='number' ? factor.score_change * 10 : factor.score_change,
                definition: factorDefinitionResult.content,
                summary: factorSummaryResult.content,
                analysis: {
                    what_this_means_for_you: Array.isArray(parsedAnalysis.what_this_means_for_you) ? parsedAnalysis.what_this_means_for_you : [],
                    day_to_day_impacts: Array.isArray(parsedAnalysis.day_to_day_impacts) ? parsedAnalysis.day_to_day_impacts : [],
                    impactful_quotes: Array.isArray(parsedAnalysis.impactful_quotes) ? parsedAnalysis.impactful_quotes : []
                },
                key_takeaways: Array.isArray(factorTakeaways) ? factorTakeaways : [],
                strategic_focus: Array.isArray(factorStrategicFocus) ? factorStrategicFocus : []
            });
        }

        // Top 3 factors analysis
        const top3FactorsAnalysis = [];
        for (const factor of top3FactorsForAnalysis) {
            const factorSummaryPrompt = `
            Based on the following data for this culture factor, provide a single sentence summary that captures the most important insight:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide one clear, concise sentence that summarizes the most critical insight about this factor's performance. The sentence should:
            - Capture the essence of what's happening with this factor
            - Don't reference the score
            - The simplest sentence that best describes the theme of the feedback for this factor
            - Be easily understood by leadership
            - A Single sentence that isn't too long
            
            Return only the single sentence summary, no additional formatting or explanation.
            `;
            const factorSummaryResult = await callLLM(factorSummaryPrompt, updateTokens, selectedModel);

            const factorAnalysisPrompt = `
            Based on the following data for this culture factor, provide analysis covering what this means, day-to-day impacts, and a thematic analysis:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Please provide a JSON response with the following structure:
            {
                "what_this_means_for_you": ["Point 1 - one sentence", "Point 2 - one sentence", "Point 3 - one sentence"],
                "day_to_day_impacts": ["Impact 1 - one sentence", "Impact 2 - one sentence", "Impact 3 - one sentence"],
                "thematic_analysis": "Sentence 1. Sentence 2. Sentence 3."
            }
            
            **Guidelines:**
            - "what_this_means_for_you": Provide exactly 3 points, each as one clear sentence explaining what this factor specifically means in the simplest terms
            - "day_to_day_impacts": Provide exactly 3 points, each as one sentence describing concrete daily effects people experience related to this factor
            - "thematic_analysis": Provide exactly 3 sentences that explain the core theme present in the feedback for this factor; write as a short, cohesive paragraph of three sentences
            - Be specific and actionable
            - Use clear, jargon-free language
            - Base insights on the actual feedback provided
            `;
            const factorAnalysisResult = await callLLM(factorAnalysisPrompt, updateTokens, selectedModel);
            const parsedAnalysis = parseGPTJsonResponse(factorAnalysisResult.content);

            top3FactorsAnalysis.push({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: typeof factor.score_change==='number' ? factor.score_change * 10 : factor.score_change,
                summary: factorSummaryResult.content,
                what_this_means_for_you: Array.isArray(parsedAnalysis.what_this_means_for_you) ? parsedAnalysis.what_this_means_for_you : [],
                day_to_day_impacts: Array.isArray(parsedAnalysis.day_to_day_impacts) ? parsedAnalysis.day_to_day_impacts : [],
                thematic_analysis: typeof parsedAnalysis.thematic_analysis === 'string' ? parsedAnalysis.thematic_analysis : Array.isArray(parsedAnalysis.thematic_analysis) ? parsedAnalysis.thematic_analysis.join(' ') : ""
            });
        }

        // Overview analysis for combined
        const overviewPrompt = `
        Please analyze the following data for organization name: ${anchorName}
        ---
        **Scores**  
        - Latest: ${Math.round(overallAverage * 10 * 10) / 10}  
        - Previous: ${Math.round(lastOverallAverage * 10 * 10) / 10}

        **Factor Scores**
        ${changeDataWithFeedback.map(f=>`- ${f.factor_name}: ${f.latest_score} - \n\n Most recent Feedback ${f.latest_feedback}`).join("\n")}
        ---
        
        Provide JSON formatted response with the following structure:
        1. Overview of the feedback in 3-4 sentences.
        2. 3 Quotes from Feedback.
        3. Key takeaways (4 items).
        4. Next steps - 3-4 sentences.
        
        **JSON Format**
        {
            "overview": "",
            "quotes": [],
            "key_takeaways": [],
            "next_steps": ""
        }
        `;
        const overviewResult = await callLLM(overviewPrompt, updateTokens, selectedModel);
        const overviewAnalysis = parseGPTJsonResponse(overviewResult.content);

        // Build key metrics like per-anchor path
        const orgDataAll = raw_data?.[0]?.responses || [];
        const anchorDataAll = filteredData || [];
        const keyMetrics = [
            ...computeCultureOrgAnchorMetrics(orgDataAll, anchorDataAll, questions, lastFilteredData),
            ...computeOutcomeOrgAnchorMetrics(orgDataAll, anchorDataAll, outcomeQuestions, lastFilteredData)
        ];

        const anchorReport = {
            anchor_id: "combined",
            anchor_name: anchorName,
            overall_score: overallAverage,
            previous_overall_score: lastOverallAverage,
            score_change: (overallAverage - lastOverallAverage) * 10,
            participation_rate: null,
            total_feedback_count: totalFeedbackCount,
            overview_analysis: {
                overview: overviewAnalysis.overview || "",
                quotes: Array.isArray(overviewAnalysis.quotes) ? overviewAnalysis.quotes : [],
                key_takeaways: Array.isArray(overviewAnalysis.key_takeaways) ? overviewAnalysis.key_takeaways : [],
                next_steps: overviewAnalysis.next_steps || ""
            },
            all_factors: changeDataWithFeedback.map(factor => ({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: typeof factor.score_change==='number' ? factor.score_change * 10 : factor.score_change
            })),
            lowest_3_factors_analysis: detailedFactorAnalysis,
            top_3_factors_analysis: top3FactorsAnalysis,
            analysis_type: hasSignificantChanges ? "biggest_changes" : "top_scores",
            outcome_scores: {
                latest: latestOutcomeScores,
                previous: lastOutcomeScores
            },
            key_metrics: keyMetrics
        };
        anchorReports.push(anchorReport);
    } else {
        // Iterate through each of the selected anchor options in the selected anchor parent
        for (const option of optionsToProcess) {
            const anchorID = option.id;
            const anchorName = option.name;
            const filteredData = filter_data(raw_data[0].responses, [{id: anchorID}]);
            const lastFilteredData = filter_data(raw_data[0].last, [{id: anchorID}]);
                
        // Get the anchor data
        const latestFactorScores = calculateAverageScoresPerFactor(filteredData);
        const lastFactorScores = calculateAverageScoresPerFactor(lastFilteredData);
        
        // Calculate outcome scores - calculate per outcome section and merge to maintain original structure
        const latestOutcomeScores = {};
        const lastOutcomeScores = {};
        if (outcomeQuestions && outcomeQuestions.questions) {
            outcomeQuestions.questions.forEach(outcomeSection => {
                const latestSectionScores = calculateAverageScoresPerOutcome(filteredData, outcomeQuestions, outcomeSection.id);
                const lastSectionScores = calculateAverageScoresPerOutcome(lastFilteredData, outcomeQuestions, outcomeSection.id);
                
                // Merge section scores into the main objects to maintain original JSON structure
                Object.assign(latestOutcomeScores, latestSectionScores);
                Object.assign(lastOutcomeScores, lastSectionScores);
            });
        }
        
        const overallAverage = get_overall_average(filteredData);
        const lastOverallAverage = get_overall_average(lastFilteredData);
        
        // Count total feedback received for this anchor
        const totalFeedbackCount = filteredData.length;

        // Get the specific participation data for this anchor
        // NOTE: Temporarily disabled – participationRate is an empty array while
        // anchor participation calculation is turned off.
        // const currentAnchorParticipation = participationRate.find(p => p.anchor_id === anchorID);
        const currentAnchorParticipation = null;

        const changeDataWithFeedback = getChangeData(latestFactorScores, lastFactorScores, questions, filteredData, lastFilteredData);

        // Sort factors by latest_score to identify lowest scoring factors
        const factorsByScore = [...changeDataWithFeedback].sort((a, b) => {
            const scoreA = typeof a.latest_score === 'number' ? a.latest_score : 0;
            const scoreB = typeof b.latest_score === 'number' ? b.latest_score : 0;
            return scoreA - scoreB; // Ascending order (lowest first)
        });

        // Get the 3 lowest scoring factors
        const lowest3Factors = factorsByScore
            .filter(f => typeof f.latest_score === 'number')
            .slice(0, 3);

        // Calculate top 3 biggest changes or top 3 scores if no significant changes
        const factorsByChange = [...changeDataWithFeedback]
            .filter(f => typeof f.latest_score === 'number' && typeof f.last_score === 'number')
            .sort((a, b) => b.score_change - a.score_change); // Sort by absolute change

        // If no significant changes (all changes < 5%), fall back to top 3 highest scores
        const hasSignificantChanges = factorsByChange.some(f => f.score_change >= 0.5);
        
        let top3FactorsForAnalysis;
        if (hasSignificantChanges) {
            top3FactorsForAnalysis = factorsByChange.slice(0, 3);
        } else {
            // Fall back to top 3 highest scoring factors
            top3FactorsForAnalysis = [...changeDataWithFeedback]
                .filter(f => typeof f.latest_score === 'number')
                .sort((a, b) => b.latest_score - a.latest_score)
                .slice(0, 3);
        }

        // Generate detailed LLM analysis for each of the 3 lowest factors
        const detailedFactorAnalysis = [];
        for (const factor of lowest3Factors) {
            const factorAnalysisPrompt = `
            Please analyze the following feedback data for this underperforming culture factor:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            
            **Latest Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Please provide a JSON response with the following structure:
            {
                "what_this_means_for_you": ["Point 1 - one sentence", "Point 2 - one sentence", "Point 3 - one sentence"],
                "day_to_day_impacts": ["Impact 1 - one sentence", "Impact 2 - one sentence", "Impact 3 - one sentence"],
                "impactful_quotes": ["Quote 1", "Quote 2", "Quote 3", "Quote 4", "Quote 5", "Quote 6"]
            }
            
            **Guidelines:**
            - "what_this_means_for_you": Provide exactly 3 points, each as one clear sentence explaining what this low score specifically means in the simplest terms
            - "day_to_day_impacts": Provide exactly 3 points, each as one sentence describing concrete daily effects employees experience due to this factor's low performance
            - "impactful_quotes": Extract up to 6 of the most impactful, meaningful quotes directly from the feedback provided. For English quotes, use the exact wording. For quotes in any other language, you MUST translate the entire quote text to English first, then add "(translated)" at the end. The quote text itself must be in English. If fewer than 6 impactful quotes exist, provide what's available.
            - Be specific and actionable
            - Focus on practical implications
            - Use clear, jargon-free language
            - Base insights on the actual feedback provided
            `;
            
            const gptResult = await callLLM(factorAnalysisPrompt, updateTokens, selectedModel);
            const parsedAnalysis = parseGPTJsonResponse(gptResult.content);

            // Generate key takeaways for this specific factor
            const factorTakeawaysPrompt = `
            Based on the following data for this specific culture factor, provide 4 key takeaways:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide 4 key takeaways specifically for this factor. Each takeaway should be:
            - A single, clear sentence
            - Specific to this factor's performance and feedback
            - Summarative of the key themes in the feedback
            - Based on the data and feedback provided
            
            Return only a JSON array of strings:
            ["takeaway 1", "takeaway 2", "takeaway 3", "takeaway 4"]
            `;
            
            const factorTakeawaysResult = await callLLM(factorTakeawaysPrompt, updateTokens, selectedModel);
            const factorTakeaways = parseGPTJsonResponse(factorTakeawaysResult.content);

            // Generate strategic focus for this specific factor
            const factorStrategicFocusPrompt = `
            Based on the following data for this specific culture factor, provide 3 strategic considerations for leadership:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            
            **Factor Challenges from Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide exactly 3 strategic considerations that represent the most important things for leaders to strategize based on all the feedback. Each should be:
            - A concise final thought about what leaders should strategize/consider
            - Based directly on the feedback patterns and themes
            - Actionable strategic guidance (not just tasks)
            - Focused on leadership decision-making and priorities
            
            Return only a JSON array of 3 strings:
            ["strategic consideration 1", "strategic consideration 2", "strategic consideration 3"]
            `;
            
            const factorStrategicFocusResult = await callLLM(factorStrategicFocusPrompt, updateTokens, selectedModel);
            const factorStrategicFocus = parseGPTJsonResponse(factorStrategicFocusResult.content);

            // Generate 1 sentence summary for this factor
            const factorSummaryPrompt = `
            Based on the following data for this culture factor, provide a single sentence summary that captures the most important insight:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide one clear, concise sentence that summarizes the most critical insight about this factor's performance. The sentence should:
            - Capture the essence of what's happening with this factor
            - Don't reference the score
            - The simplest sentence that best describes the theme of the feedback for this factor
            - Be easily understood by leadership
            - A Single sentence that isn't too long
            
            Return only the single sentence summary, no additional formatting or explanation.
            `;
            
            const factorSummaryResult = await callLLM(factorSummaryPrompt, updateTokens, selectedModel);

            // Generate factor definition based on organizational context and feedback
            const factorDefinitionPrompt = `
            Based on the following culture factor and organizational feedback, provide a clear definition of what this factor means for organizations:
            
            **Factor:** ${factor.factor_name}
            
            Create a definition that explains:
            - What this factor represents in the workplace
            
            The definition should be:
            - 1 sentence long
            - Written in clear, professional language
            - Applicable to organizational leaders and HR professionals
            
            Return only the definition, no additional formatting or explanation.
            `;
            
            const factorDefinitionResult = await callLLM(factorDefinitionPrompt, updateTokens, selectedModel);
            
            // Find the benchmark score for this factor
            let benchmarkScore = null;
            if (standards?.response?.pillars) {
                // Extract factor ID and dimension ID from the factor.factor_id (e.g., "0_1" -> factor=0, dimension=1)
                const factorParts = factor.factor_id.split("_");
                const factorId = parseInt(factorParts[0], 10);
                const dimensionId = parseInt(factorParts[1], 10);
                
                // Find matching pillar in standards that matches both factor and dimension (id)
                const matchingPillar = standards.response.pillars.find(pillar => 
                    pillar.factor === factorId && pillar.id === dimensionId
                );
                if (matchingPillar) {
                    benchmarkScore = matchingPillar.average;
                }
            }
            
            detailedFactorAnalysis.push({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: factor.score_change * 10, // Convert to percentage
                benchmark_score: benchmarkScore,
                definition: factorDefinitionResult.content,
                summary: factorSummaryResult.content,
                analysis: {
                    what_this_means_for_you: Array.isArray(parsedAnalysis.what_this_means_for_you) ? parsedAnalysis.what_this_means_for_you : [],
                    day_to_day_impacts: Array.isArray(parsedAnalysis.day_to_day_impacts) ? parsedAnalysis.day_to_day_impacts : [],
                    impactful_quotes: Array.isArray(parsedAnalysis.impactful_quotes) ? parsedAnalysis.impactful_quotes : []
                },
                key_takeaways: Array.isArray(factorTakeaways) ? factorTakeaways : [],
                strategic_focus: Array.isArray(factorStrategicFocus) ? factorStrategicFocus : []
            });
        } 

        // Generate analysis for top 3 biggest changes or top 3 scores
        const top3FactorsAnalysis = [];
        for (const factor of top3FactorsForAnalysis) {
            // Generate 1 sentence summary for this factor
            const factorSummaryPrompt = `
            Based on the following data for this culture factor, provide a single sentence summary that captures the most important insight:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Provide one clear, concise sentence that summarizes the most critical insight about this factor's performance. The sentence should:
            - Capture the essence of what's happening with this factor
            - Don't reference the score
            - The simplest sentence that best describes the theme of the feedback for this factor
            - Be easily understood by leadership
            - A Single sentence that isn't too long
            
            Return only the single sentence summary, no additional formatting or explanation.
            `;
            
            const factorSummaryResult = await callLLM(factorSummaryPrompt, updateTokens, selectedModel);

            // Generate analysis mirroring the lowest 3 structure with thematic analysis
            const factorAnalysisPrompt = `
            Based on the following data for this culture factor, provide analysis covering what this means, day-to-day impacts, and a thematic analysis:
            
            **Organization:** ${anchorName}
            **Factor:** ${factor.factor_name}
            **Current Score:** ${factor.latest_score.toFixed(2)}
            **Previous Score:** ${factor.last_score !== "No data" ? factor.last_score.toFixed(2) : "No previous data"}
            **Score Change:** ${factor.score_change >= 0 ? '+' : ''}${(factor.score_change * 10).toFixed(1)}%
            
            **Factor Feedback:**
            ${factor.latest_feedback || "No feedback provided"}
            
            Please provide a JSON response with the following structure:
            {
                "what_this_means_for_you": ["Point 1 - one sentence", "Point 2 - one sentence", "Point 3 - one sentence"],
                "day_to_day_impacts": ["Impact 1 - one sentence", "Impact 2 - one sentence", "Impact 3 - one sentence"],
                "thematic_analysis": "Sentence 1. Sentence 2. Sentence 3."
            }
            
            **Guidelines:**
            - "what_this_means_for_you": Provide exactly 3 points, each as one clear sentence explaining what this factor specifically means in the simplest terms
            - "day_to_day_impacts": Provide exactly 3 points, each as one sentence describing concrete daily effects people experience related to this factor
            - "thematic_analysis": Provide exactly 3 sentences that explain the core theme present in the feedback for this factor; write as a short, cohesive paragraph of three sentences
            - Be specific and actionable
            - Use clear, jargon-free language
            - Base insights on the actual feedback provided
            `;
            
            const factorAnalysisResult = await callLLM(factorAnalysisPrompt, updateTokens, selectedModel);
            const parsedAnalysis = parseGPTJsonResponse(factorAnalysisResult.content);

            top3FactorsAnalysis.push({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: factor.score_change * 10, // Convert to percentage
                summary: factorSummaryResult.content,
                what_this_means_for_you: Array.isArray(parsedAnalysis.what_this_means_for_you) ? parsedAnalysis.what_this_means_for_you : [],
                day_to_day_impacts: Array.isArray(parsedAnalysis.day_to_day_impacts) ? parsedAnalysis.day_to_day_impacts : [],
                thematic_analysis: typeof parsedAnalysis.thematic_analysis === 'string' ? parsedAnalysis.thematic_analysis : Array.isArray(parsedAnalysis.thematic_analysis) ? parsedAnalysis.thematic_analysis.join(' ') : ""
            });
        }





        // Generate overview analysis for this anchor
        const overviewPrompt = `
        Please analyze the following data for organization name: ${anchorName}
        ---
        **Scores**  
        - Latest: ${Math.round(overallAverage * 10 * 10) / 10}  
        - Previous: ${Math.round(lastOverallAverage * 10 * 10) / 10}

        **Factor Scores**
        ${changeDataWithFeedback.map(f=>`- ${f.factor_name}: ${f.latest_score} - \n\n Most recent Feedback ${f.latest_feedback}`).join("\n")}
        ---
        
        Provide JSON formatted response with the following structure:
        1. Overview of the feedback in 3-4 sentences. Focus on explaining what the bigger themes are in the feedback that explain the overall scores.
        2. 3 Quotes from Feedback that illustrate the biggest shifts or themes. Just include the quote text only in a list.
        3. Key takeaways (4 key takeaways). Focus on bullet point, high level key feedback takeaways.
        4. Next steps - 3-4 sentences on what the overall feedback means and where to focus next steps.
    
        **Tone & Format**  
        - Keep it clear, concise, and professional.
        - Aim for an engaging, insight-focused style that helps readers quickly grasp the main points.
        - Even though we are anchoring the analysis in complex culture factors, we want to communicate things in a simple way without the jargon.
        - Never use markdown syntax for anything, just plain text.
        - Never add "-" in front of any bullet points and never use markdown syntax for bullet points.

        **JSON Format**
        {
            "overview": "",
            "quotes": [],
            "key_takeaways": [],
            "next_steps": ""
        }
        `;
        
        const overviewResult = await callLLM(overviewPrompt, updateTokens, selectedModel);
        const overviewAnalysis = parseGPTJsonResponse(overviewResult.content);
        // If parsing failed, surface the raw output to the console for debugging
        if (overviewAnalysis && overviewAnalysis.overview === "Error parsing response") {
          console.log("[manager_v3] Overview JSON parse failed. Raw LLM output:\n", overviewResult?.content);
        }

        // Build org vs anchor key metrics (factors and outcomes with per-question breakdown)
        const orgDataAll = raw_data?.[0]?.responses || [];
        const anchorDataAll = filteredData || [];

        const keyMetrics = [
            ...computeCultureOrgAnchorMetrics(orgDataAll, anchorDataAll, questions, lastFilteredData),
            ...computeOutcomeOrgAnchorMetrics(orgDataAll, anchorDataAll, outcomeQuestions, lastFilteredData)
        ];

        // Build the individual report for this anchor
        const anchorReport = {
            anchor_id: anchorID,
            anchor_name: anchorName,
            overall_score: overallAverage,
            previous_overall_score: lastOverallAverage,
            score_change: (overallAverage - lastOverallAverage) * 10, // Convert to percentage
            participation_rate: currentAnchorParticipation,
            total_feedback_count: totalFeedbackCount,
            overview_analysis: {
                overview: overviewAnalysis.overview || "",
                quotes: Array.isArray(overviewAnalysis.quotes) ? overviewAnalysis.quotes : [],
                key_takeaways: Array.isArray(overviewAnalysis.key_takeaways) ? overviewAnalysis.key_takeaways : [],
                next_steps: overviewAnalysis.next_steps || ""
            },
            all_factors: changeDataWithFeedback.map(factor => ({
                factor_name: factor.factor_name,
                latest_score: factor.latest_score,
                last_score: factor.last_score,
                score_change: factor.score_change * 10 // Convert to percentage
            })),
            lowest_3_factors_analysis: detailedFactorAnalysis,
            top_3_factors_analysis: top3FactorsAnalysis,
            analysis_type: hasSignificantChanges ? "biggest_changes" : "top_scores",
            outcome_scores: {
                latest: latestOutcomeScores,
                previous: lastOutcomeScores
            },
            key_metrics: keyMetrics
        };
        
        anchorReports.push(anchorReport);
    }
}

    // Calculate final costs
    const inputCost = (totalTokenUsage.prompt_tokens / 1000000) * 2;
    const outputCost = (totalTokenUsage.completion_tokens / 1000000) * 8;
    const totalCost = inputCost + outputCost;

    // Return restructured data with individual reports per anchor
    return {
        overall_summary: {
            overall_average: overallLatestScore,
            overall_change: overallChange * 10, // Convert to percentage
            previous_average: overallLastScore,
            benchmark_average: standards?.response?.overall?.average || null
        },
        overall_factor_scores: overallFactorData.map(factor => ({
            factor_name: factor.factor_name,
            latest_score: factor.latest_score,
            previous_score: factor.last_score,
            score_change: factor.score_change * 10 // Convert to percentage
        })),
        selected_anchor_parent: {
            id: selectedAnchorParent.id,
            name: selectedAnchorParent.name
        },
        anchor_reports: anchorReports,
        standards: standards || null,
        token_usage: {
            ...totalTokenUsage,
            input_cost: inputCost,
            output_cost: outputCost,
            total_cost: totalCost
        }
    };
}




/**
 * Calculate the average scores per culture factor.
 * Each entry in data is expected to have a "questions" array.
 * We use a composite key "factor_dimension" (e.g. "0_1") to track each factor.
 */
function calculateAverageScoresPerFactor(data) {
  console.log(data)
    const factorScores = {};
    data.forEach(entry => {
      const questions = entry.questions || [];
      questions.forEach(question => {
        const factor = question.factor;
        const dimension = question.id; // assuming "id" represents the dimension id
        const response = question.response;
        // Only process if we have valid numeric response (filter out 'N/A' values)
        if (factor != null && dimension != null && isValidNumericResponse(response)) {
          const key = `${factor}_${dimension}`;
          if (factorScores[key]) {
            factorScores[key].sum += response;
            factorScores[key].count += 1;
          } else {
            factorScores[key] = { sum: response, count: 1, factor, dimension };
          }
        }
      });
    });
    // Calculate averages for each factor key
    const averages = {};
    Object.keys(factorScores).forEach(key => {
      // Handle case where all responses were 'N/A' (count would be 0)
      if (factorScores[key].count > 0) {
        averages[key] = factorScores[key].sum / factorScores[key].count;
      } else {
        averages[key] = "No data"; // or could be null/undefined
      }
    });
    return averages;
  }

/**
 * Calculate the average scores and high score percentage for employee outcomes.
 * Uses the exact same method as heatmap calculations for consistency.
 * @param {Array} data - Array of response data
 * @param {Object} outcomeQuestions - Outcome questions structure
 * @param {string} selectedOutcomeId - ID of the specific outcome section to process
 */
function calculateAverageScoresPerOutcome(data, outcomeQuestions, selectedOutcomeId = null) {
    const results = {};
    
    if (!outcomeQuestions || !outcomeQuestions.questions) {
        return results;
    }
    
    // Filter outcome questions by selectedOutcomeId if provided (same as heatmap)
    const questionsToProcess = selectedOutcomeId 
        ? outcomeQuestions.questions.filter(question => String(question.id) === String(selectedOutcomeId))
        : outcomeQuestions.questions;
    
    questionsToProcess.forEach((question) => {
        let name = question.name;
        let filtered_responses = data
            .map((entry) =>
                entry?.employee_outcomes?.responses
                    ?.filter((i) => {
                        return (
                            i.q == question.id &&
                            (i?.clicked === true || i?.clicked === undefined)
                        );
                    })
                    .map((i) => i?.response)
            )
            .flat()
            .filter((f) => !isNaN(f) && f !== undefined && f !== null);

        if (filtered_responses.length > 0) {
            const sum = filtered_responses.reduce((acc, val) => acc + val, 0);
            const average = sum / filtered_responses.length;
            
            // Calculate percentage of responses that are 7 or above
            const highScores = filtered_responses.filter(response => response >= 7);
            const highScorePercentage = (highScores.length / filtered_responses.length) * 100;
            
            // Calculate NPS components
            const promoters = filtered_responses.filter(response => response >= 9);
            const detractors = filtered_responses.filter(response => response <= 6);
            const promoterPercentage = (promoters.length / filtered_responses.length) * 100;
            const detractorPercentage = (detractors.length / filtered_responses.length) * 100;
            const npsScore = promoterPercentage - detractorPercentage;
            
            results[question.id] = {
                outcome_name: name,
                average_score: average,
                high_score_percentage: highScorePercentage,
                total_responses: filtered_responses.length,
                nps: {
                    promoter_percentage: promoterPercentage,
                    detractor_percentage: detractorPercentage,
                    nps_score: npsScore
                }
            };
        } else {
            results[question.id] = {
                outcome_name: name,
                average_score: "No data",
                high_score_percentage: 0,
                total_responses: 0,
                nps: {
                    promoter_percentage: 0,
                    detractor_percentage: 0,
                    nps_score: 0
                }
            };
        }
    });
    
    return results;
}

  function filterFeedback(responses, factor, dimension) {
    if (!responses || !Array.isArray(responses)) return [];
    
    const feedbackList = [];
    responses.forEach(entry => {
      const questions = entry.feedback || [];
      questions.forEach(question => {
        if (question.factor === factor && question.id === dimension && question.feedback) {
          feedbackList.push({
            feedback: question.feedback,
            question: question.question || "",
            follow_up: question.follow_up || "",
            follow_up_question: question.follow_up_question || "",
            prompt_question: question.prompt_question || ""
          });
        }
      });
    });
    
    return feedbackList;
  }

  function getFactorFeedback(factorTuple, responses, options = {}) {
    if (!factorTuple) return "";
    const [factor, dimension] = factorTuple;
    
    // Filter the responses
    const feedbackList = filterFeedback(responses, factor, dimension);
    if (!feedbackList.length) return "";
    
    // Depending on options, choose a formatting strategy. For now, we use one format.
    return formatFeedback(feedbackList, options);
  }
  
  function formatFeedback(feedbackList, options = {}) {
    if (!feedbackList.length) return "";
    
    const { headerName } = options;
    let formatted = headerName
      ? `### Feedback from ${headerName} responses:\n\n`
      : "### Feedback from responses:\n\n";
    
    feedbackList.forEach(item => {
      // If prompt_question exists, handle it differently
      if (item.prompt_question) {
        formatted += "Follow up: " + item.prompt_question + "\n";
        formatted += "Response: " + (item.feedback || "") + "\n";
        formatted += "\n";
        return;
      }
      
      if (item.feedback) {
        formatted += "Question asked: " + (item.question || "") + "\n";
        formatted += "Response: " + item.feedback + "\n";
      }
      
      if (item.follow_up && item.follow_up_question) {
        formatted += "Follow up: " + item.follow_up_question + "\n";
        formatted += "Response: " + item.follow_up + "\n";
      }
      
      formatted += "\n";
    });
    
    formatted += "### END OF FEEDBACK \n\n";
    return formatted;
  }

  function getFactorName(questionsStructure, factorTuple) {
    if (!factorTuple) return null;
    const [factorId, dimensionId] = factorTuple;
    const dimensions = questionsStructure.dimensions || [];
    // Find the dimension whose id equals dimensionId+1 (adjust if necessary)
    const dimension = dimensions.find(dim => dim.id === dimensionId + 1);
    if (!dimension) return null;
    const factor = dimension.factors.find(fact => fact.id === factorId + 1);
    if (!factor) return null;
    return factor.title;
  }

  // --- Org vs Anchor key metrics helpers ---
  function computeCultureOrgAnchorMetrics(orgData, anchorData, questionsStructure, lastAnchorData = null){
    if(!questionsStructure?.dimensions) return [];

    const results = [];

    questionsStructure.dimensions.forEach((dimension, dimIndex)=>{
      dimension.factors.forEach((factor, factorIndex)=>{
        const orgAvg = averageQuestionSet(orgData, dimIndex, factorIndex, questionsStructure);
        const anchorAvg = averageQuestionSet(anchorData, dimIndex, factorIndex, questionsStructure);
        const hasLastAnchor = Array.isArray(lastAnchorData) && lastAnchorData.length > 0;
        const lastAnchorAvg = hasLastAnchor
          ? averageQuestionSet(lastAnchorData, dimIndex, factorIndex, questionsStructure)
          : undefined;

        const questionsArray = factor.questions.map((qObj, localIndex)=>{
          const orgQ = averageSingleQuestion(orgData, dimIndex, factorIndex, localIndex, questionsStructure);
          const anchorQ = averageSingleQuestion(anchorData, dimIndex, factorIndex, localIndex, questionsStructure);
          const item = { question: qObj.q, org_score: orgQ, anchor_score: anchorQ };
          if(hasLastAnchor){
            const lastAnchorQ = averageSingleQuestion(lastAnchorData, dimIndex, factorIndex, localIndex, questionsStructure);
            item.last_anchor_score = lastAnchorQ;
          }
          return item;
        });

        const factorItem = { title: factor.title, org_score: orgAvg, anchor_score: anchorAvg, questions: questionsArray };
        if(hasLastAnchor){
          factorItem.last_anchor_score = lastAnchorAvg;
        }
        results.push(factorItem);
      })
    })

    return results;
  }

  function averageQuestionSet(data, dimIndex, factorIndex, structure){
    let sum = 0; let count = 0;
    data?.forEach(resp=>{
      resp?.questions?.forEach(q=>{
        // questions use zero-based ids in data: q.id == dimension index, q.factor == factor index
        if(q?.id === dimIndex && q?.factor === factorIndex){
          const val = applyReverseIfNeeded(q, structure);
          if(isValidNumericResponse(val)){ sum += val; count += 1; }
        }
      })
    })
    return count ? (sum / count) : 0;
  }

  function averageSingleQuestion(data, dimIndex, factorIndex, localIndex, structure){
    let sum = 0; let count = 0;
    data?.forEach(resp=>{
      resp?.questions?.forEach(q=>{
        if(q?.id === dimIndex && q?.factor === factorIndex && q?.q === localIndex){
          const val = applyReverseIfNeeded(q, structure);
          if(isValidNumericResponse(val)){ sum += val; count += 1; }
        }
      })
    })
    return count ? (sum / count) : 0;
  }

  function applyReverseIfNeeded(questionItem, structure){
    try{
      const dim = structure?.dimensions?.find(d=>d.id === (questionItem.id+1));
      const fac = dim?.factors?.find(f=>f.id === (questionItem.factor+1));
      const qDef = fac?.questions?.[questionItem.q];
      if(qDef?.reverse){
        if(typeof questionItem.response === 'number'){
          return 10 - questionItem.response;
        }
      }
    }catch(e){/* ignore lookup issues; fall through */}
    return questionItem.response;
  }

  function computeOutcomeOrgAnchorMetrics(orgData, anchorData, outcomeDef, lastAnchorData = null){
    if(!outcomeDef?.questions) return [];
    // Build an index of outcome questions by "q-s" and group them by section name
    const { questionIndex, sectionToQuestions } = buildOutcomeIndex(outcomeDef);

    const orgAgg = aggregateOutcomeResponses(orgData, questionIndex);
    const anchorAgg = aggregateOutcomeResponses(anchorData, questionIndex);
    const hasLastAnchor = Array.isArray(lastAnchorData) && lastAnchorData.length > 0;
    const lastAnchorAgg = hasLastAnchor ? aggregateOutcomeResponses(lastAnchorData, questionIndex) : null;

    // Format results per section maintaining question order from definition
    const results = [];
    Object.keys(sectionToQuestions).forEach(sectionName => {
      const questionKeys = sectionToQuestions[sectionName];
      let orgSum = 0; let orgCount = 0; let anchorSum = 0; let anchorCount = 0; let lastAnchorSum = 0; let lastAnchorCount = 0;

      const questions = questionKeys.map(key => {
        const meta = questionIndex[key];
        const org = orgAgg.questions[key] || { sum: 0, count: 0 };
        const anc = anchorAgg.questions[key] || { sum: 0, count: 0 };
        const lastAnc = hasLastAnchor && lastAnchorAgg ? (lastAnchorAgg.questions[key] || { sum: 0, count: 0 }) : { sum: 0, count: 0 };
        const orgAvg = org.count ? (org.sum / org.count) : 0;
        const ancAvg = anc.count ? (anc.sum / anc.count) : 0;
        const lastAncAvg = lastAnc.count ? (lastAnc.sum / lastAnc.count) : 0;
        orgSum += org.sum; orgCount += org.count; anchorSum += anc.sum; anchorCount += anc.count; lastAnchorSum += lastAnc.sum; lastAnchorCount += lastAnc.count;
        const item = { question: meta.text, org_score: orgAvg, anchor_score: ancAvg };
        if(hasLastAnchor){ item.last_anchor_score = lastAncAvg; }
        return item;
      });

      const sectionOrgAvg = orgCount ? (orgSum / orgCount) : 0;
      const sectionAnchorAvg = anchorCount ? (anchorSum / anchorCount) : 0;
      const sectionItem = { title: sectionName, org_score: sectionOrgAvg, anchor_score: sectionAnchorAvg, questions };
      if(hasLastAnchor){
        const sectionLastAnchorAvg = lastAnchorCount ? (lastAnchorSum / lastAnchorCount) : 0;
        sectionItem.last_anchor_score = sectionLastAnchorAvg;
      }
      results.push(sectionItem);
    });

    return results;
  }

  function buildOutcomeIndex(outcomeDef){
    const questionIndex = {}; // key => {section, text}
    const sectionToQuestions = {}; // section => [keys]
    outcomeDef.questions.forEach(section => {
      const sectionName = section.name;
      section.questions.forEach(q => {
        const key = `${q.q}-${q.s}`;
        questionIndex[key] = { section: sectionName, text: q.question };
        if(!sectionToQuestions[sectionName]) sectionToQuestions[sectionName] = [];
        sectionToQuestions[sectionName].push(key);
      })
    })
    return { questionIndex, sectionToQuestions };
  }

  function aggregateOutcomeResponses(data, questionIndex){
    const byQuestion = {}; // key => {sum,count}
    const bySection = {}; // section => {sum,count}
    data?.forEach(resp => {
      const arr = resp?.employee_outcomes?.responses || [];
      arr.forEach(item => {
        // Respect clicked flag semantics used elsewhere
        if(!(item?.clicked === true || item?.clicked === undefined)) return;
        const key = `${item.q}-${item.s}`;
        if(!questionIndex[key]) return; // skip responses not in def
        const val = typeof item.response === 'number' ? item.response : null;
        if(!isValidNumericResponse(val)) return;
        if(!byQuestion[key]) byQuestion[key] = { sum: 0, count: 0 };
        byQuestion[key].sum += val; byQuestion[key].count += 1;
        const section = questionIndex[key].section;
        if(!bySection[section]) bySection[section] = { sum: 0, count: 0 };
        bySection[section].sum += val; bySection[section].count += 1;
      })
    })
    return { questions: byQuestion, sections: bySection };
  }

  function getChangeData(latestFactorScores, lastFactorScores, questions, filteredData = null, lastFilteredData = null){

    // Get the union of all factor keys (composite keys: "factor_dimension")
    const allFactorKeys = new Set([
        ...Object.keys(latestFactorScores),
        ...Object.keys(lastFactorScores)
      ]);

      const factorChanges = [];

       // Process each culture factor sequentially
       for (let key of allFactorKeys) {
        const parts = key.split("_");
        const factorId = parseInt(parts[0], 10);
        const dimensionId = parseInt(parts[1], 10);
    
        const factorName = getFactorName(questions, [factorId, dimensionId]);
        const latestScore =
          latestFactorScores[key] !== undefined
            ? latestFactorScores[key]
            : "No data";
        const lastScore =
          lastFactorScores[key] !== undefined ? lastFactorScores[key] : "No data";

        // Track the numeric score change
        const scoreChange =
          typeof latestScore === "number" && typeof lastScore === "number"
            ? latestScore - lastScore
            : 0;

        const latestFeedback = filteredData ? getFactorFeedback(
            [factorId, dimensionId],
            filteredData
          ) : "";
          const lastFeedback = lastFilteredData ? getFactorFeedback(
            [factorId, dimensionId],
            lastFilteredData
          ) : "";

        factorChanges.push({
          factor_id: key,
          factor_name: factorName,
          latest_score: latestScore,
          last_score: lastScore,
          score_change: scoreChange,
          latest_feedback: latestFeedback,
          last_feedback: lastFeedback
        });
    }

    return factorChanges;
  }